/**
 * Hook for registering push tokens from React Native WebView
 * Continuously checks for token and registers it with the server
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { registerPushToken } from '../services/api-client';
import { pushTokenLogger } from '../utils/pushTokenLogger';
import { getStoredExpoPushToken } from '../utils/expo-push-token';

type ExpoPushTokenAvailableEvent = CustomEvent<{ token?: string }>;

export function usePushTokenRegistration() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const hasRegistered = useRef(false);
  const checkInterval = useRef<NodeJS.Timeout>();

  // Log when hook is mounted
  useEffect(() => {
    pushTokenLogger.log('🎯 usePushTokenRegistration hook monterad');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      hasRegistered.current = false;
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      return;
    }

    pushTokenLogger.log('✅ Användare autentiserad, startar token-kontroll');

    const checkAndRegisterToken = async () => {
      if (hasRegistered.current) return;

      // Check for token from multiple sources
      const token = getStoredExpoPushToken();

      if (typeof window !== 'undefined' && window.expoPushToken) {
        console.log('[usePushTokenRegistration] Found token on window.expoPushToken');
      }

      if (!window.expoPushToken && token) {
        console.log('[usePushTokenRegistration] Found token in localStorage');
      }

      if (token) {
        console.log('[usePushTokenRegistration] Attempting to register token...');
        try {
          const result = await registerPushToken(token);
          if (result.success) {
            console.log('[usePushTokenRegistration] ✅ Token registered successfully');
            hasRegistered.current = true;
            
            // Clear interval once registered
            if (checkInterval.current) {
              clearInterval(checkInterval.current);
            }
          } else {
            console.error('[usePushTokenRegistration] ❌ Failed to register token:', result.error);
          }
        } catch (e) {
          console.error('[usePushTokenRegistration] ❌ Exception while registering token:', e);
        }
      } else {
        console.log('[usePushTokenRegistration] No token found yet, will check again...');
      }
    };

    // Check immediately
    checkAndRegisterToken();

    // Then check every 2 seconds for up to 30 seconds
    let attempts = 0;
    const maxAttempts = 15;
    
    checkInterval.current = setInterval(() => {
      attempts++;
      if (attempts >= maxAttempts) {
        console.log('[usePushTokenRegistration] Stopped checking after', maxAttempts, 'attempts');
        if (checkInterval.current) {
          clearInterval(checkInterval.current);
        }
        return;
      }
      
      checkAndRegisterToken();
    }, 2000);

    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
    };
  }, [isAuthenticated]);

  // Also listen for the custom event
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleTokenEvent = async (event: Event) => {
      const customEvent = event as ExpoPushTokenAvailableEvent;
      const token = customEvent.detail?.token;
      console.log('[usePushTokenRegistration] Received expoPushTokenAvailable event');
      
      if (token && !hasRegistered.current) {
        console.log('[usePushTokenRegistration] Registering token from event...');
        try {
          const result = await registerPushToken(token);
          if (result.success) {
            console.log('[usePushTokenRegistration] ✅ Token registered from event');
            hasRegistered.current = true;
            
            // Clear interval
            if (checkInterval.current) {
              clearInterval(checkInterval.current);
            }
          } else {
            console.error('[usePushTokenRegistration] ❌ Failed to register token from event:', result.error);
          }
        } catch (e) {
          console.error('[usePushTokenRegistration] ❌ Exception while registering token from event:', e);
        }
      }
    };

    window.addEventListener('expoPushTokenAvailable', handleTokenEvent);
    
    return () => {
      window.removeEventListener('expoPushTokenAvailable', handleTokenEvent);
    };
  }, [isAuthenticated]);
}
